import { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import Button from '../button/Button';
import Icon from '../icon/Icon';
import Input from '../input/Input';
import CurrencyInput from '../input/CurrencyInput';
import CheckBox from '../checkBox/CheckBox';
import { OverdueData, formatOverdueGridAmount } from '../../utils/overdueColumns';
import { formatCuit } from '../../utils/Masks';
import { updateGuaranteeStatus, updateLoanRate, createPartialPayment, CurrencyQuotation, markCollectedOk, markUncollectible, restoreOverdue, getConfrontationContext, ConfrontationContextData } from '../../services/GuaranteesService';
import { useToast } from '../toast/toast';
import { getQuotations, Quotation } from '../../services/QuotationsService';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { resolveCurrencyCode } from '../../utils/currencyLabel';

type ConfirmAction = 'collected_ok' | 'uncollectible' | null;
type RestoreDialog = 'confirm' | 'scope' | null;

/** Catalog names from guarantees.status_types.name (stable across environments). */
const STATUS_NAME = {
  VENCIDA: 'Vencida',
  AFRONTADA: 'Afrontada',
  HONRADA: 'Honrada',
  PENDIENTE: 'Pendiente',
  AFRONTADA_PAGO_PARCIAL: 'Afrontada con pago parcial',
  INCOBRABLE: 'Incobrable',
  CANCELADO_ANTICIPADAMENTE: 'Cancelado anticipadamente',
} as const;

/** Local scoped brown style for Restablecer — do not change Button.styles / tokens. */
const restoreButtonStyle: React.CSSProperties = {
  backgroundColor: '#A05A00',
  borderColor: '#A05A00',
  color: '#FFFFFF',
};

const restoreWarningBoxStyle: React.CSSProperties = {
  backgroundColor: '#FDF6EC',
  border: '1px solid #B88C4B',
  borderRadius: '8px',
  padding: '12px 16px',
};

const restoreScopeCardStyle: React.CSSProperties = {
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  padding: '16px',
};

const sectionLabelStyle: React.CSSProperties = {
  marginBottom: '12px',
  color: '#374151',
};

const otherOptionsLabelStyle: React.CSSProperties = {
  marginBottom: '12px',
  color: '#6B7280',
};

const footerCancelStyle: React.CSSProperties = {
  borderTop: '1px solid #E9ECEF',
  paddingTop: '16px',
  marginTop: '8px',
};

interface OverdueStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  overdueItem: OverdueData | null;
  onSuccess: () => void;
}

const OverdueStatusModal: React.FC<OverdueStatusModalProps> = ({
  isOpen,
  onClose,
  overdueItem,
  onSuccess,
}) => {
  const fiscalId = useFiscalIdConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [ratePercentage, setRatePercentage] = useState('');
  const [honrarMontoCompleto, setHonrarMontoCompleto] = useState(true);
  const [montoHonrado, setMontoHonrado] = useState<number | null>(null);
  const [currencyQuotations, setCurrencyQuotations] = useState<CurrencyQuotation[]>([]);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [restoreDialog, setRestoreDialog] = useState<RestoreDialog>(null);
  // New state for afrontar mode
  const [afrontarDate, setAfrontarDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [afrontarAmount, setAfrontarAmount] = useState<number | null>(null);
  const [afrontarAmountError, setAfrontarAmountError] = useState<string | null>(null);
  // New state for honrar mode
  const [honrarDate, setHonrarDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  // Asset/quotation context from backend
  const [assetContext, setAssetContext] = useState<ConfrontationContextData | null>(null);
  const [inputUnit, setInputUnit] = useState<'PESOS' | 'ASSET'>('PESOS');
  const [assetQuotationOverride, setAssetQuotationOverride] = useState<number | null>(null);
  const [assetQuantityInput, setAssetQuantityInput] = useState<number | null>(null);

  const getTotalAmount = () => {
    if (!overdueItem) return 0;
    return overdueItem.amount || overdueItem.monetization_amount;
  };

  const isLoanInstallmentType = () => {
    if (!overdueItem) return false;
    // Overdue API serializes InstrumentType.displayName ("Prestamo"), not the enum name.
    const normalized = overdueItem.type
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
    return normalized === 'PRESTAMO' || normalized === 'LOAN';
  };

  const getAlreadyHonoredAmount = () => {
    if (!overdueItem) return 0;
    return overdueItem.partial_payment_amount ?? overdueItem.honored_amount ?? 0;
  };

  const getPendingAmount = () => {
    if (!overdueItem) return 0;
    return getTotalAmount() - getAlreadyHonoredAmount();
  };

  const isHonorFlowStatus = () => {
    if (!overdueItem) return false;
    return (
      overdueItem.status_type === STATUS_NAME.AFRONTADA ||
      overdueItem.status_type === STATUS_NAME.AFRONTADA_PAGO_PARCIAL
    );
  };

  const isDueDateBeforeToday = (dueDate?: string | null) => {
    if (!dueDate) return false;
    const date = new Date(`${dueDate}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isAfrontarOnlyState = () => {
    if (!overdueItem) return false;
    return (
      overdueItem.status_type === STATUS_NAME.PENDIENTE ||
      overdueItem.status_type === STATUS_NAME.CANCELADO_ANTICIPADAMENTE ||
      overdueItem.badge === 'PENDIENTE' ||
      overdueItem.badge === 'CANCELADO_ANTICIPADAMENTE'
    );
  };

  const isOverdueForHonorOrConfront = () => {
    if (!overdueItem || isAfrontarOnlyState()) return false;
    const type = overdueItem.type;
    const isCommercialEndorsement = type === 'Aval Comercial' || type === 'AVAL_COMERCIAL';
    const dueDateBeforeToday = isDueDateBeforeToday(overdueItem.due_date);

    if (isCommercialEndorsement) return dueDateBeforeToday;
    return overdueItem.status_type === STATUS_NAME.VENCIDA;
  };

  const shouldBlockHonorOrConfront = () => {
    if (!overdueItem) return false;
    return isOverdueForHonorOrConfront() && overdueItem.monetization_status_type_id !== 2;
  };

  /**
   * Extract a human-readable message from an axios-style rejection.
   * Handles different HTTP status codes with specific messages.
   */
  const extractErrorMessage = (error: unknown, fallback: string): string => {
    const axiosError = error as { 
      response?: { 
        status?: number; 
        data?: { message?: string; error?: string } 
      } 
    };
    
    const status = axiosError?.response?.status;
    const backendMessage = axiosError?.response?.data?.message || axiosError?.response?.data?.error;
    
    // Return specific messages based on HTTP status codes
    switch (status) {
      case 400:
        return backendMessage || 'Solicitud inválida. Verifique los datos ingresados.';
      case 401:
        return 'Sesión expirada. Por favor, vuelva a iniciar sesión.';
      case 403:
        return 'No tiene permisos para realizar esta acción.';
      case 404:
        return backendMessage || 'El instrumento no fue encontrado.';
      case 409:
        return backendMessage || 'El instrumento ya fue procesado por otro usuario.';
      case 422:
        return backendMessage || 'No se puede procesar: el instrumento no cumple los requisitos.';
      case 500:
        return 'Error del servidor. Intente nuevamente más tarde.';
      case 503:
        return 'Servicio no disponible. Intente nuevamente más tarde.';
      default:
        return typeof backendMessage === 'string' && backendMessage.trim() !== '' 
          ? backendMessage 
          : fallback;
    }
  };

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const response = await getQuotations();
        if (response?.success && 'data' in response && Array.isArray(response.data)) {
          const quotations: CurrencyQuotation[] = response.data.map((q: Quotation) => ({
            asset_code: q.code,
            sale_value: typeof q.saleValue === 'string' ? parseFloat(q.saleValue) : q.saleValue,
          }));
          setCurrencyQuotations(quotations);
        }
      } catch (error) {
        console.error('Error fetching quotations:', error);
      }
    };

    if (isOpen) {
      fetchQuotations();
      const initialAmount = overdueItem
        ? overdueItem.amount || overdueItem.monetization_amount || null
        : null;
      setAfrontarAmount(initialAmount);
      setAfrontarAmountError(null);
      setAssetContext(null);
      setInputUnit('PESOS');
      setAssetQuotationOverride(null);
      setAssetQuantityInput(null);
      setHonrarMontoCompleto(true);
      setMontoHonrado(null);

      if (overdueItem) {
        const fetchContext = async () => {
          try {
            const ctx = await getConfrontationContext(
              parseInt(overdueItem.id_global),
              overdueItem.installment_id || undefined
            );
            setAssetContext(ctx);
            if (ctx.asset_quotation) setAssetQuotationOverride(ctx.asset_quotation);

            const isHonor =
              overdueItem.status_type === STATUS_NAME.AFRONTADA ||
              overdueItem.status_type === STATUS_NAME.AFRONTADA_PAGO_PARCIAL;

            if (ctx.scenario === 3 && ctx.asset_quotation != null) {
              const nativeQty =
                ctx.proposed_quantity ??
                (ctx.pending_quantity != null && ctx.pending_quantity > 0 ? ctx.pending_quantity : null) ??
                overdueItem.amount ??
                overdueItem.monetization_amount ??
                null;
              if (nativeQty != null && nativeQty > 0) {
                setAssetQuantityInput(nativeQty);
                const pesosAmount =
                  ctx.pending_amount_ars != null && ctx.pending_amount_ars > 0
                    ? ctx.pending_amount_ars
                    : Math.round(nativeQty * ctx.asset_quotation * 100) / 100;
                if (isHonor) {
                  setMontoHonrado(pesosAmount);
                } else {
                  setAfrontarAmount(pesosAmount);
                }
              }
            } else if (ctx.proposed_quantity != null) {
              setAssetQuantityInput(ctx.proposed_quantity);
            }
          } catch (err) {
            console.error('Error fetching confrontation context:', err);
          }
        };
        fetchContext();
      }
    }
  }, [isOpen]);

  const handleUpdateStatus = async (status: number) => {
    if (!overdueItem) return;

    const isLoanConfront = status === 2 && isLoanInstallmentType();
    const hasAsset = isAssetScenario();
    const confrontAmount = hasAsset
      ? (inputUnit === 'PESOS' ? afrontarAmount : getEquivalentPesos())
      : (afrontarAmount ?? getTotalAmount());

    if (isLoanConfront || hasAsset) {
      if (confrontAmount == null || confrontAmount <= 0) {
        const msg = inputUnit === 'ASSET' && hasAsset ? 'La cantidad debe ser mayor a 0' : 'El monto debe ser mayor a 0';
        setAfrontarAmountError(msg);
        useToast.error({ title: msg, id: 'overdue-toast', autoClose: 5000 });
        return;
      }
      if (hasAsset) {
        const q = getEffectiveQuotation();
        if (!q || q <= 0) {
          useToast.error({ title: 'La cotización debe ser mayor a cero', id: 'overdue-toast', autoClose: 5000 });
          return;
        }
      }
      setAfrontarAmountError(null);
    }

    setIsLoading(true);
    try {
      const options: Record<string, unknown> = {};
      if (status === 2) {
        options.confrontationDate = afrontarDate || undefined;
      }
      if (isLoanConfront || hasAsset) {
        options.amount = confrontAmount;
      }
      if (hasAsset && assetContext) {
        options.assetId = assetContext.asset_id;
        options.assetQuantity = getEquivalentQuantity();
        options.assetQuotation = getEffectiveQuotation();
        options.inputUnit = inputUnit;
      }

      await updateGuaranteeStatus(
        parseInt(overdueItem.id_global),
        overdueItem.installment_number,
        status,
        currencyQuotations,
        Object.keys(options).length > 0 ? options as { amount?: number; confrontationDate?: string; assetId?: number; assetQuantity?: number; assetQuotation?: number; inputUnit?: string } : undefined
      );
      useToast.success({
        title: status === 2
          ? 'Cuota marcada como afrontada exitosamente'
          : 'Cuota marcada como honrada exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: extractErrorMessage(error, 'Error al actualizar el estado de la cuota'),
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error updating guarantee status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRate = async () => {
    if (!overdueItem || !ratePercentage) {
      useToast.error({
        title: 'Ingrese un porcentaje válido',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateLoanRate(
        overdueItem.id,
        overdueItem.installment_number,
        parseFloat(ratePercentage)
      );
      useToast.success({
        title: 'Tasa actualizada exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: 'Error al actualizar la tasa',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error updating loan rate:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatusWithAmount = async (status: number) => {
    if (!overdueItem) return;

    const statusType = overdueItem.status_type;
    const isAfrontada = statusType === 'Afrontada' || statusType === 'Afrontada con pago parcial';
    const isVencida = statusType === 'Vencida';

    if (!honrarMontoCompleto) {
      if (montoHonrado === null || montoHonrado <= 0) {
        useToast.error({
          title: 'Ingrese un monto válido',
          id: 'overdue-toast',
          autoClose: 5000,
        });
        return;
      }
      
      if (isAfrontada) {
        const maxAmount = isAssetScenario() && inputUnit === 'PESOS'
          ? getPendingPesosAmount()
          : getPendingAmount();
        const honorPesos = getHonorSubmitPesosAmount();
        if (honorPesos != null && honorPesos > maxAmount) {
          useToast.error({
            title: `El monto no puede superar ${maxAmount.toLocaleString('es-AR')}`,
            id: 'overdue-toast',
            autoClose: 5000,
          });
          return;
        }
      } else if (montoHonrado > overdueItem.monetization_amount) {
        useToast.error({
          title: 'El monto no puede superar el monto de monetización',
          id: 'overdue-toast',
          autoClose: 5000,
        });
        return;
      }
    }

    setIsLoading(true);
    try {
      const usePartialPayments = 
        (isAfrontada && status === 3) ||
        (isVencida && status === 3 && !honrarMontoCompleto);

      if (usePartialPayments) {
        const amount = honrarMontoCompleto
          ? getHonorSubmitPesosAmount() ?? getPendingPesosAmount()
          : getHonorSubmitPesosAmount() ?? montoHonrado!;
        
        await createPartialPayment(
          parseInt(overdueItem.id_global),
          new Date().toISOString().split('T')[0],
          honrarMontoCompleto,
          amount,
          overdueItem.installment_number
        );
        useToast.success({
          title: 'Cuota marcada como honrada exitosamente',
          id: 'overdue-toast',
          autoClose: 5000,
        });
      } else {
        await updateGuaranteeStatus(
          parseInt(overdueItem.id_global),
          overdueItem.installment_number,
          status,
          currencyQuotations
        );
        useToast.success({
          title: status === 2
            ? 'Cuota marcada como afrontada exitosamente'
            : 'Cuota marcada como honrada exitosamente',
          id: 'overdue-toast',
          autoClose: 5000,
        });
      }
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: extractErrorMessage(error, 'Error al actualizar el estado de la cuota'),
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error updating guarantee status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setHonrarMontoCompleto(checked);
    if (checked && overdueItem) {
      if (isAssetScenario() && inputUnit === 'ASSET') {
        setAssetQuantityInput(getNativePendingQuantity());
      } else if (isAssetScenario()) {
        setMontoHonrado(getPendingPesosAmount());
      } else {
        setMontoHonrado(getPendingAmount());
      }
    } else {
      setMontoHonrado(null);
    }
  };

  const handleMarkCollectedOk = async () => {
    if (!overdueItem) return;
    setIsLoading(true);
    try {
      const instrumentType = mapInstrumentType(overdueItem.type);
      await markCollectedOk(
        parseInt(overdueItem.id_global),
        instrumentType,
        overdueItem.installment_number || undefined
      );
      useToast.success({
        title: 'Marcado como Cobrada OK exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      setConfirmAction(null);
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: extractErrorMessage(error, 'Error al marcar como Cobrada OK'),
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error marking as collected OK:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkUncollectible = async () => {
    if (!overdueItem) return;
    setIsLoading(true);
    try {
      const instrumentType = mapInstrumentType(overdueItem.type);
      await markUncollectible(
        parseInt(overdueItem.id_global),
        instrumentType,
        overdueItem.installment_number || undefined
      );
      useToast.success({
        title: 'Marcado como Incobrable exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      setConfirmAction(null);
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: extractErrorMessage(error, 'Error al marcar como Incobrable'),
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error marking as uncollectible:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async (soloHonras: boolean) => {
    if (!overdueItem || isLoading) return;
    setIsLoading(true);
    try {
      const instrumentType = mapInstrumentType(overdueItem.type);
      await restoreOverdue(
        parseInt(overdueItem.id_global, 10),
        instrumentType,
        soloHonras,
        overdueItem.installment_number || undefined
      );
      useToast.success({
        title: 'Aval restablecido exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      setRestoreDialog(null);
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: extractErrorMessage(error, 'Error al restablecer el aval'),
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error restoring overdue:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const mapInstrumentType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'CHEQUE': 'CHECK',
      'Cheque': 'CHECK',
      'PAGARE': 'PAGARE',
      'Pagaré': 'PAGARE',
      'PRESTAMO': 'PRESTAMO',
      'Préstamo': 'PRESTAMO',
      'Prestamo': 'PRESTAMO',
      'AVAL_COMERCIAL': 'AVAL_COMERCIAL',
      'Aval Comercial': 'AVAL_COMERCIAL',
    };
    return typeMap[type] || type;
  };

  const canMarkCollectedOk = () => {
    if (!overdueItem || overdueItem.collected_ok || isAfrontarOnlyState()) return false;
    const statusType = overdueItem.status_type;
    return statusType === STATUS_NAME.VENCIDA;
  };

  const canMarkUncollectible = () => {
    if (!overdueItem) return false;
    const statusType = overdueItem.status_type;
    return (
      statusType === STATUS_NAME.VENCIDA ||
      statusType === STATUS_NAME.AFRONTADA ||
      statusType === STATUS_NAME.AFRONTADA_PAGO_PARCIAL
    );
  };

  const canRestore = () => {
    if (!overdueItem) return false;
    const statusType = overdueItem.status_type;
    return (
      statusType === STATUS_NAME.AFRONTADA ||
      statusType === STATUS_NAME.HONRADA ||
      statusType === STATUS_NAME.AFRONTADA_PAGO_PARCIAL
    );
  };

  const openRestoreDialog = () => {
    if (!overdueItem) return;
    if (overdueItem.status_type === STATUS_NAME.AFRONTADA) {
      setRestoreDialog('confirm');
    } else {
      setRestoreDialog('scope');
    }
  };

  const renderRestoreButton = () => {
    if (!canRestore()) return null;
    return (
      <Button
        variant='primary'
        icon='replay_outline'
        iconAlign='left'
        iconModifier='inverted'
        onClick={openRestoreDialog}
        disabled={isLoading}
        style={restoreButtonStyle}
      >
        Restablecer
      </Button>
    );
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const formatCurrencyLabel = () => {
    if (!overdueItem) return '';
    if (overdueItem.currency === 'USD' || isUsdDenominatedScenario()) return 'U$S';
    return resolveCurrencyCode(overdueItem.currency);
  };

  const isAssetScenario = () => assetContext != null && assetContext.scenario > 1;

  /** Scenario 3: instrument in foreign currency (USD) without a cotizable asset — amounts are native USD. */
  const isUsdDenominatedScenario = () => assetContext?.scenario === 3;

  const getEffectiveQuotation = () => assetQuotationOverride ?? assetContext?.asset_quotation ?? 0;

  const roundAmount = (value: number, decimals: number) =>
    Math.round(value * 10 ** decimals) / 10 ** decimals;

  const convertNativeToPesos = (nativeQty: number, quotation = getEffectiveQuotation()) =>
    roundAmount(nativeQty * quotation, 2);

  const convertPesosToNative = (pesos: number, quotation = getEffectiveQuotation()) =>
    roundAmount(pesos / quotation, 4);

  const getNativePendingQuantity = (): number => {
    if (assetContext?.pending_quantity != null && assetContext.pending_quantity > 0) {
      return assetContext.pending_quantity;
    }
    if (isUsdDenominatedScenario()) {
      return assetContext?.proposed_quantity ?? getPendingAmount();
    }
    return getPendingAmount();
  };

  const getPendingPesosAmount = (): number => {
    if (assetContext?.pending_amount_ars != null && assetContext.pending_amount_ars > 0) {
      return assetContext.pending_amount_ars;
    }
    if (isUsdDenominatedScenario()) {
      const q = getEffectiveQuotation();
      if (q > 0) return convertNativeToPesos(getNativePendingQuantity(), q);
    }
    return getPendingAmount();
  };

  const getHeaderNativeAmount = (): number => {
    if (isHonorFlowStatus()) {
      return isUsdDenominatedScenario() ? getNativePendingQuantity() : getPendingAmount();
    }
    if (isUsdDenominatedScenario()) {
      return assetContext?.proposed_quantity ?? getTotalAmount();
    }
    return getTotalAmount();
  };

  const getPesosPlaceholderAmount = () => {
    if (isHonorFlowStatus()) return getPendingPesosAmount();
    const total = getTotalAmount();
    if (isUsdDenominatedScenario()) {
      const q = getEffectiveQuotation();
      if (q > 0) return convertNativeToPesos(total, q);
    }
    return total;
  };

  const getActivePesosAmount = (): number | null => {
    if (isAssetScenario() && inputUnit === 'ASSET') {
      return assetQuantityInput != null ? convertNativeToPesos(assetQuantityInput) : null;
    }
    return isHonorFlowStatus() ? montoHonrado : afrontarAmount;
  };

  const getHonorSubmitPesosAmount = (): number | null => {
    if (!isAssetScenario()) return montoHonrado;
    if (inputUnit === 'PESOS') return montoHonrado;
    return assetQuantityInput != null ? convertNativeToPesos(assetQuantityInput) : null;
  };

  const handleInputUnitChange = (unit: 'PESOS' | 'ASSET') => {
    if (unit === inputUnit) return;

    const q = getEffectiveQuotation();
    if (q > 0) {
      if (unit === 'PESOS') {
        const nativeQty =
          assetQuantityInput ??
          assetContext?.pending_quantity ??
          assetContext?.proposed_quantity ??
          null;
        if (nativeQty != null) {
          const pesos = convertNativeToPesos(nativeQty, q);
          setAfrontarAmount(pesos);
          setMontoHonrado(pesos);
        }
      } else {
        const pesos =
          (isHonorFlowStatus() ? montoHonrado : afrontarAmount) ??
          (assetContext?.pending_amount_ars != null && assetContext.pending_amount_ars > 0
            ? assetContext.pending_amount_ars
            : null) ??
          (assetContext?.proposed_quantity != null && isUsdDenominatedScenario()
            ? convertNativeToPesos(assetContext.proposed_quantity, q)
            : null);
        if (pesos != null) {
          setAssetQuantityInput(convertPesosToNative(pesos, q));
        } else if (assetContext?.proposed_quantity != null) {
          setAssetQuantityInput(assetContext.proposed_quantity);
        } else if (assetContext?.pending_quantity != null) {
          setAssetQuantityInput(assetContext.pending_quantity);
        }
      }
    }

    setInputUnit(unit);
  };

  const getEquivalentPesos = (): number | null => {
    if (!isAssetScenario()) return null;
    return getActivePesosAmount();
  };

  const getEquivalentQuantity = (): number | null => {
    if (!isAssetScenario()) return null;
    const q = getEffectiveQuotation();
    if (!q || q <= 0) return null;
    if (inputUnit === 'PESOS') {
      const pesos = getActivePesosAmount();
      return pesos != null ? convertPesosToNative(pesos, q) : null;
    }
    return assetQuantityInput;
  };

  const getAssetLabel = () => {
    if (!assetContext) return '';
    return assetContext.asset_name || assetContext.asset_code || 'Activo';
  };

  const renderCancelFooter = () => (
    <Card display='flex' justifyContent='flex-end' style={footerCancelStyle}>
      <Button variant='contained' onClick={onClose} disabled={isLoading}>
        Cancelar
      </Button>
    </Card>
  );

  const renderContent = () => {
    const statusType = overdueItem?.status_type;
    const actionBlocked = shouldBlockHonorOrConfront();

    // --- Honrada: read-only summary + Restablecer ---
    if (statusType === 'Honrada') {
      return (
        <Card display='grid' gap='16px' margin={{ top: '4' }}>
          <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ color: '#6B7280' }}>
            Ya fue honrada
          </P>
          <Card display='flex' gap='24px' flexWrap='wrap'>
            <Card display='flex' gap='8px' alignItems='center'>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Fecha de honra:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                {overdueItem?.confrontation_date
                  ? formatDisplayDate(overdueItem.confrontation_date)
                  : formatDisplayDate(new Date().toISOString().split('T')[0])}
              </P>
            </Card>
            <Card display='flex' gap='8px' alignItems='center'>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Honrado:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                {formatCurrencyLabel()} {(overdueItem?.honored_amount ?? getTotalAmount()).toLocaleString('es-AR')}
              </P>
            </Card>
          </Card>
          <Card style={{ borderTop: '1px solid #E9ECEF', paddingTop: '16px', marginTop: '8px' }}>
            <P type='3' variant='neutral' weight='bold' withMargin={false} style={otherOptionsLabelStyle}>
              Otras opciones
            </P>
            <Card display='flex' gap='12px' flexWrap='wrap'>
              {renderRestoreButton()}
            </Card>
          </Card>
          {renderCancelFooter()}
        </Card>
      );
    }

    // --- Afrontada: honor flow ---
    if (statusType === 'Afrontada' || statusType === 'Afrontada con pago parcial') {
      const pendingAmount = getPendingAmount();
      const hasAsset = isAssetScenario();
      const pendingNativeQty = getNativePendingQuantity();
      const pendingPesos = getPendingPesosAmount();

      return (
        <Card display='grid' gap='16px' margin={{ top: '4' }}>
          <P type='3' variant='neutral' weight='bold' withMargin={false} style={sectionLabelStyle}>
            Honrar
          </P>
          {hasAsset && (
            <Card display='flex' gap='12px' alignItems='center'>
              <Button
                variant={inputUnit === 'PESOS' ? 'primary' : 'tertiary'}
                onClick={() => handleInputUnitChange('PESOS')}
                disabled={isLoading || actionBlocked}
                style={{ minWidth: '80px' }}
              >
                Pesos
              </Button>
              <Button
                variant={inputUnit === 'ASSET' ? 'primary' : 'tertiary'}
                onClick={() => handleInputUnitChange('ASSET')}
                disabled={isLoading || actionBlocked}
                style={{ minWidth: '140px', whiteSpace: 'nowrap' }}
              >
                {getAssetLabel()}
              </Button>
            </Card>
          )}

          <Card display='flex' gap='12px' alignItems='start' flexWrap='wrap'>
            <Card style={{ minWidth: '140px', flex: 1 }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                Fecha de honra
              </P>
              <Input
                type='date'
                value={honrarDate}
                onChange={(e) => setHonrarDate(e.target.value)}
                disabled={isLoading || actionBlocked}
              />
            </Card>
            <Card style={{ minWidth: '100px', flex: 1 }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                Pendiente {hasAsset ? `(${assetContext?.asset_code || 'Activo'})` : ''}
              </P>
              <Input
                type='text'
                value={hasAsset
                  ? pendingNativeQty.toLocaleString('es-AR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                  : pendingAmount.toLocaleString('es-AR')
                }
                disabled
              />
              {hasAsset && (
                <P type='3' variant='neutral' withMargin={false} style={{ marginTop: '2px', fontSize: '11px', color: '#6B7280' }}>
                  ≈ $ {pendingPesos.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </P>
              )}
            </Card>
            {hasAsset && (
              <Card style={{ minWidth: '130px', flex: 1 }}>
                <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                  Cotización ({assetContext?.asset_code || 'Activo'})
                </P>
                <CurrencyInput
                  placeholder='Cotización'
                  value={assetQuotationOverride}
                  onValueChange={(val) => setAssetQuotationOverride(val)}
                  disabled={isLoading || actionBlocked}
                />
              </Card>
            )}

            <Card style={{ minWidth: '160px', flex: 1.5 }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                {hasAsset && inputUnit === 'ASSET'
                  ? `Cantidad (${assetContext?.asset_code || 'Activo'})`
                  : hasAsset
                    ? 'Monto (ARS)'
                    : 'Monto'}
              </P>
              <CurrencyInput
                placeholder={hasAsset && inputUnit === 'PESOS'
                  ? getPendingPesosAmount().toLocaleString('es-AR')
                  : 'Ingrese el monto'}
                value={hasAsset && inputUnit === 'ASSET' ? assetQuantityInput : montoHonrado}
                onValueChange={(val) => {
                  if (hasAsset && inputUnit === 'ASSET') {
                    setAssetQuantityInput(val);
                  } else {
                    setMontoHonrado(val);
                  }
                }}
                disabled={isLoading || honrarMontoCompleto || actionBlocked}
                readOnly={honrarMontoCompleto}
              />
            </Card>
            <Button
              variant='success'
              onClick={() => handleUpdateStatusWithAmount(3)}
              disabled={isLoading || actionBlocked || (hasAsset && (!getEffectiveQuotation() || getEffectiveQuotation() <= 0))}
              style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
            >
              Marcar como honrada
            </Button>
          </Card>

          {hasAsset && (
            <Card display='flex' gap='16px' style={{ padding: '8px 12px', backgroundColor: '#F3F4F6', borderRadius: '8px' }}>
              {inputUnit === 'ASSET' ? (
                <P type='3' variant='neutral' withMargin={false}>
                  Monto en pesos: <strong>$ {(getEquivalentPesos() ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </P>
              ) : (
                <P type='3' variant='neutral' withMargin={false}>
                  Equivale a: <strong>{(getEquivalentQuantity() ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {assetContext?.asset_code || 'unidades'}</strong>
                </P>
              )}
            </Card>
          )}

          <CheckBox
            checked={honrarMontoCompleto}
            onChange={handleCheckboxChange}
            disabled={isLoading || actionBlocked}
          >
            {hasAsset ? 'Honrar cantidad completa' : 'Honrar monto completo'}
          </CheckBox>

          <Card style={{ borderTop: '1px solid #E9ECEF', paddingTop: '16px', marginTop: '8px' }}>
            <P type='3' variant='neutral' weight='bold' withMargin={false} style={otherOptionsLabelStyle}>
              Otras opciones
            </P>
            <Card display='flex' gap='12px' flexWrap='wrap'>
              {canMarkUncollectible() && (
                <Button
                  variant='danger'
                  onClick={() => setConfirmAction('uncollectible')}
                  disabled={isLoading}
                >
                  Incobrable
                </Button>
              )}
              {renderRestoreButton()}
            </Card>
          </Card>
          {renderCancelFooter()}
        </Card>
      );
    }

    // --- Vencida / Pendiente / Cancelado anticipadamente: confront flow ---
    if (
      statusType === 'Vencida' ||
      statusType === 'Pendiente' ||
      statusType === 'Cancelado anticipadamente'
    ) {
      const afrontarOnly = isAfrontarOnlyState();
      const hasAsset = isAssetScenario();

      return (
        <Card display='grid' gap='16px' margin={{ top: '4' }}>
          <P type='3' variant='neutral' weight='bold' withMargin={false} style={sectionLabelStyle}>
            Afrontar
          </P>
          {hasAsset && (
            <Card display='flex' gap='12px' alignItems='center'>
              <Button
                variant={inputUnit === 'PESOS' ? 'primary' : 'tertiary'}
                onClick={() => handleInputUnitChange('PESOS')}
                disabled={isLoading || actionBlocked}
                style={{ minWidth: '80px' }}
              >
                Pesos
              </Button>
              <Button
                variant={inputUnit === 'ASSET' ? 'primary' : 'tertiary'}
                onClick={() => handleInputUnitChange('ASSET')}
                disabled={isLoading || actionBlocked}
                style={{ minWidth: '140px', whiteSpace: 'nowrap' }}
              >
                {getAssetLabel()}
              </Button>
            </Card>
          )}

          <Card display='flex' gap='12px' alignItems='start' flexWrap='wrap'>
            <Card style={{ flex: 1, minWidth: '140px' }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                Fecha
              </P>
              <Input
                type='date'
                value={afrontarDate}
                onChange={(e) => setAfrontarDate(e.target.value)}
                disabled={isLoading || actionBlocked}
              />
            </Card>
            {hasAsset && inputUnit === 'ASSET' ? (
              <Card style={{ flex: 1, minWidth: '140px' }}>
                <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                  Cantidad ({assetContext?.asset_code || 'Activo'})
                </P>
                <CurrencyInput
                  placeholder={assetContext?.proposed_quantity?.toLocaleString('es-AR', { maximumFractionDigits: 4 }) || '0'}
                  value={assetQuantityInput}
                  onValueChange={(val) => setAssetQuantityInput(val)}
                  disabled={isLoading || actionBlocked}
                />
              </Card>
            ) : (
              <Card style={{ flex: 1, minWidth: '140px' }}>
                <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                  Monto (ARS)
                </P>
                <CurrencyInput
                  placeholder={getPesosPlaceholderAmount().toLocaleString('es-AR')}
                  value={afrontarAmount}
                  onValueChange={(val) => {
                    setAfrontarAmount(val);
                    if (isLoanInstallmentType() || hasAsset) {
                      setAfrontarAmountError(
                        val == null || val <= 0 ? 'El monto debe ser mayor a 0' : null
                      );
                    }
                  }}
                  disabled={isLoading || actionBlocked}
                />
                {afrontarAmountError && (
                  <P type='3' variant='danger' withMargin={false} style={{ marginTop: '4px' }}>
                    {afrontarAmountError}
                  </P>
                )}
              </Card>
            )}

            {hasAsset && (
              <Card style={{ flex: 1, minWidth: '140px' }}>
                <P type='3' variant='neutral' weight='bold' withMargin={false} style={{ marginBottom: '4px' }}>
                  Cotización ({assetContext?.asset_code || 'Activo'})
                </P>
                <CurrencyInput
                  placeholder='Cotización'
                  value={assetQuotationOverride}
                  onValueChange={(val) => setAssetQuotationOverride(val)}
                  disabled={isLoading || actionBlocked}
                />
                {(!assetQuotationOverride || assetQuotationOverride <= 0) && assetContext?.asset_quotation == null && (
                  <P type='3' variant='danger' withMargin={false} style={{ marginTop: '4px', fontSize: '12px' }}>
                    Sin cotización para la fecha seleccionada
                  </P>
                )}
              </Card>
            )}

            <Button
              variant='success'
              onClick={() => handleUpdateStatus(2)}
              disabled={isLoading || actionBlocked || (hasAsset && (!getEffectiveQuotation() || getEffectiveQuotation() <= 0))}
              style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
            >
              Marcar como afrontada
            </Button>
          </Card>

          {hasAsset && (
            <Card display='flex' gap='16px' style={{ padding: '8px 12px', backgroundColor: '#F3F4F6', borderRadius: '8px' }}>
              {inputUnit === 'ASSET' ? (
                <P type='3' variant='neutral' withMargin={false}>
                  Monto en pesos: <strong>$ {(getEquivalentPesos() ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </P>
              ) : (
                <P type='3' variant='neutral' withMargin={false}>
                  Equivale a: <strong>{(getEquivalentQuantity() ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {assetContext?.asset_code || 'unidades'}</strong>
                </P>
              )}
            </Card>
          )}

          {!afrontarOnly && (
            <Card style={{ borderTop: '1px solid #E9ECEF', paddingTop: '16px', marginTop: '8px' }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false} style={otherOptionsLabelStyle}>
                Otras opciones
              </P>
              <Card display='flex' gap='12px' flexWrap='wrap'>
                <Button
                  variant='success'
                  onClick={() => handleUpdateStatus(3)}
                  disabled={isLoading || actionBlocked}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Marcar como honrada
                </Button>
                {canMarkCollectedOk() && (
                  <Button
                    variant='primary'
                    onClick={() => setConfirmAction('collected_ok')}
                    disabled={isLoading}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Marcar como cobrada
                  </Button>
                )}
                <Button
                  variant='danger'
                  onClick={() => setConfirmAction('uncollectible')}
                  disabled={isLoading}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Incobrable
                </Button>
              </Card>
            </Card>
          )}
          {renderCancelFooter()}
        </Card>
      );
    }

    return null;
  };

  const renderConfirmationDialog = () => {
    if (!confirmAction) return null;

    const isCollectedOk = confirmAction === 'collected_ok';
    const title = isCollectedOk
      ? '¿Confirmar Cobrada OK?'
      : '¿Confirmar Incobrable?';
    const message = isCollectedOk
      ? 'El deudor pagó directamente al acreedor. Este instrumento ya no requiere gestión.'
      : 'Este instrumento se marcará como incobrable y no se podrá recuperar.';

    return (
      <Card
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}
        onClick={() => setConfirmAction(null)}
      >
        <Card
          style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <P type='2' weight='bold' withMargin={false}>{title}</P>
          <Card margin={{ top: '3', bottom: '4' }}>
            <P type='3' variant='neutral'>{message}</P>
          </Card>
          <Card display='flex' gap='16px' justifyContent='flex-end'>
            <Button
              variant='tertiary'
              onClick={() => setConfirmAction(null)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant={isCollectedOk ? 'success' : 'danger'}
              onClick={isCollectedOk ? handleMarkCollectedOk : handleMarkUncollectible}
              disabled={isLoading}
            >
              {isLoading ? 'Procesando...' : 'Confirmar'}
            </Button>
          </Card>
        </Card>
      </Card>
    );
  };

  const renderRestoreDialog = () => {
    if (!restoreDialog) return null;

    const overlayStyle: React.CSSProperties = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001,
    };

    const panelStyle: React.CSSProperties = {
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '8px',
      maxWidth: restoreDialog === 'scope' ? '560px' : '440px',
      width: '90%',
    };

    if (restoreDialog === 'confirm') {
      return (
        <Card style={overlayStyle} onClick={() => !isLoading && setRestoreDialog(null)}>
          <Card style={panelStyle} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
              <P type='2' weight='bold' withMargin={false}>
                Restablecer aval
              </P>
              <Icon
                name='cancel_outline'
                cursor={isLoading ? 'not-allowed' : 'pointer'}
                onClick={() => !isLoading && setRestoreDialog(null)}
                variant='neutral'
              />
            </Card>
            <P type='3' variant='neutral' withMargin={false}>
              Esta acción no se puede deshacer. Se eliminarán todos los afrontes asociados y{' '}
              <strong>el aval volverá al estado Vencida</strong>.
            </P>
            <Card style={{ ...restoreWarningBoxStyle, marginTop: '16px', marginBottom: '16px' }}>
              <P type='3' variant='neutral' withMargin={false}>
                Los afrontes eliminados no se pueden recuperar.
              </P>
            </Card>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              ¿Confirma la operación?
            </P>
            <Card display='flex' gap='16px' justifyContent='flex-end' margin={{ top: '4' }}>
              <Button
                variant='contained'
                onClick={() => setRestoreDialog(null)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                variant='success'
                onClick={() => handleRestore(false)}
                disabled={isLoading}
              >
                {isLoading ? 'Procesando...' : 'Sí, restablecer'}
              </Button>
            </Card>
          </Card>
        </Card>
      );
    }

    return (
      <Card style={overlayStyle} onClick={() => !isLoading && setRestoreDialog(null)}>
        <Card style={panelStyle} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
            <P type='2' weight='bold' withMargin={false}>
              Restablecer aval
            </P>
            <Icon
              name='cancel_outline'
              cursor={isLoading ? 'not-allowed' : 'pointer'}
              onClick={() => !isLoading && setRestoreDialog(null)}
              variant='neutral'
            />
          </Card>
          <P type='3' variant='neutral' withMargin={false} style={{ marginBottom: '16px' }}>
            Este aval tiene honras registradas. Elegí el alcance del restablecimiento:
          </P>

          <Card display='grid' gap='12px'>
            <Card
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              gap='16px'
              style={restoreScopeCardStyle}
            >
              <Card>
                <P type='3' variant='neutral' weight='bold' withMargin={false}>
                  Eliminar solo honras
                </P>
                <P type='3' variant='neutral' withMargin={false} style={{ marginTop: '4px' }}>
                  Se eliminan las honras registradas. El afronte se mantiene y el aval pasa a{' '}
                  <strong>Afrontada</strong>.
                </P>
              </Card>
              <Button
                variant='success'
                onClick={() => handleRestore(true)}
                disabled={isLoading}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {isLoading ? 'Procesando...' : 'Seleccionar'}
              </Button>
            </Card>

            <Card
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              gap='16px'
              style={restoreScopeCardStyle}
            >
              <Card>
                <P type='3' variant='neutral' weight='bold' withMargin={false}>
                  Eliminar afronte y honras
                </P>
                <P type='3' variant='neutral' withMargin={false} style={{ marginTop: '4px' }}>
                  Se eliminan el afronte y todas las honras registradas. El aval vuelve a{' '}
                  <strong>Vencida</strong>.
                </P>
              </Card>
              <Button
                variant='danger'
                onClick={() => handleRestore(false)}
                disabled={isLoading}
                style={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  backgroundColor: '#8B4513',
                  borderColor: '#8B4513',
                }}
              >
                {isLoading ? 'Procesando...' : 'Seleccionar'}
              </Button>
            </Card>
          </Card>

          <Card style={{ ...restoreWarningBoxStyle, marginTop: '16px' }}>
            <P type='3' variant='neutral' withMargin={false}>
              Ninguna de las dos opciones se puede deshacer: los registros eliminados no se recuperan.
            </P>
          </Card>

          <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
            <Button
              variant='contained'
              onClick={() => setRestoreDialog(null)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
          </Card>
        </Card>
      </Card>
    );
  };

  if (!overdueItem) return null;

  const currencyLabel = formatCurrencyLabel();

  return (
    <>
    {renderConfirmationDialog()}
    {renderRestoreDialog()}
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: {
          background: 'rgba(0,0,0,0.65)',
          zIndex: 1,
        },
        content: {
          alignSelf: 'center',
          borderRadius: '16px',
          width: '750px',
          height: 'auto',
          maxHeight: '90vh',
          margin: '0 auto',
          padding: '24px 32px',
          overflow: 'auto',
        },
      }}
      shouldFocusAfterRender={false}
      ariaHideApp={false}
    >
      {/* Header */}
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '4' }}>
        <P type='2' variant='neutral' weight='bold' withMargin={false}>
          Modificar estado de cuota
        </P>
        <Icon name='cancel_outline' cursor='pointer' onClick={onClose} variant='neutral' />
      </Card>

      {/* Info rows — Figma 2-column header */}
      <Card display='grid' gap='12px'>
        <Card display='flex' justifyContent='space-between' gap='24px'>
          <Card display='flex' gap='8px' width='45%'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              {fiscalId.label}:
            </P>
            <P type='3' variant='neutral' withMargin={false}>
              {formatCuit(overdueItem.cuit || '')}
            </P>
          </Card>
          <Card display='flex' gap='8px' width='50%'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              Razón Social:
            </P>
            <P type='3' variant='neutral' withMargin={false}>
              {overdueItem.company_name}
            </P>
          </Card>
        </Card>

        <Card display='flex' justifyContent='space-between' gap='24px'>
          <Card display='flex' gap='8px' width='45%'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              Nº cuota:
            </P>
            <P type='3' variant='neutral' withMargin={false}>
              {overdueItem.installment_number && overdueItem.total_installments
                ? `${overdueItem.installment_number} de ${overdueItem.total_installments}`
                : '-'}
            </P>
          </Card>
          <Card display='flex' gap='8px' width='50%'>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              {isHonorFlowStatus() ? 'Monto a honrar:' : 'Monto:'}
            </P>
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              {currencyLabel}{' '}
              {formatOverdueGridAmount(getHeaderNativeAmount())}
            </P>
          </Card>
        </Card>
      </Card>

      {/* Rate update (Prestamo with rate_type_id = 2) */}
      {isLoanInstallmentType() && overdueItem.rate_type_id === 2 && (
        <Card display='flex' gap='16px' margin={{ top: '4' }} alignItems='center'>
          <Input
            type='number'
            placeholder={overdueItem.rate_percentage?.toString() || ''}
            value={ratePercentage}
            onChange={(e) => setRatePercentage(e.target.value)}
            disabled={isLoading}
          />
          <Button
            variant='success'
            onClick={handleUpdateRate}
            disabled={isLoading || !ratePercentage}
          >
            Guardar
          </Button>
        </Card>
      )}

      {renderContent()}
    </ReactModal>
    </>
  );
};

export default OverdueStatusModal;
